import React, { useEffect, useState, useMemo } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { geoMercator } from "d3-geo";

const TexasMap = ({ countyData, mapMode, onCountyClick, zoom }) => {

  const [geoJsonData, setGeoJsonData] = useState(null);
  const [tooltipContent, setTooltipContent] = useState("");
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  // Process countyData: ensure each name includes "County" if it’s missing
  const processedCountyData = useMemo(() => {
    const newCountyData = {};
    Object.keys(countyData).forEach((countyName) => {
      const formattedName = countyName.toLowerCase().includes("county")
        ? countyName
        : `${countyName} County`;
      newCountyData[formattedName] = countyData[countyName];
    });
    return newCountyData;
  }, [countyData]);

  // console.log("countyData", countyData);

  // 1) Find the maximum aggregator value among all counties
  // const maxValue = useMemo(() => {
  //   const values = Object.values(processedCountyData);
  //   if (values.length === 0) return 0;
  //   return Math.max(...values);
  // }, [processedCountyData]);
  const maxValue = 100

  // 2) A helper function to interpolate from gray (#cccccc) to bright yellow (#ffed00).
  const getColorForValue = (value, maxVal) => {
    // If there's no max or it's zero, fallback to gray
    if (!maxVal || maxVal === 0) {
      return "#cccccc";
    }
    const ratio = value / maxVal; // range [0..1]

    // Start color = #cccccc => (204, 204, 204)
    // End color   = #ffed00 => (255, 237,   0)
    const start = { r: 204, g: 204, b: 204 };
    const end = { r: 255, g: 237, b: 0 };

    const r = Math.round(start.r + (end.r - start.r) * ratio);
    const g = Math.round(start.g + (end.g - start.g) * ratio);
    const b = Math.round(start.b + (end.b - start.b) * ratio);

    return `rgb(${r}, ${g}, ${b})`;
  };

  // 🔸 Fetch the GeoJSON from the public folder
  useEffect(() => {
    fetch("/filtered_tx_counties.json")
      .then((res) => res.json())
      .then((data) => setGeoJsonData(data))
      .catch((err) => console.error("Error fetching GeoJSON:", err));
  }, []);
  
  const handleShapeClick = (countyName) => {
    if (onCountyClick) {
      onCountyClick(countyName);
    }
  };

  return (
    <div className="map-wrapper" style={{ position: "relative" }}>
      <h2>Texas County Data</h2>
      <p>Hover over a county to see details</p>

      {/* Render the map only after geoJsonData is loaded */}
      {geoJsonData && (
        <ComposableMap
          projection={geoMercator().scale(6000).center([-98.5, 28.5])}
          style={{ width: "100%", height: "100%" }}
        >
          <Geographies geography={geoJsonData}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const countyName = geo.properties.COUNTY;
                const value = processedCountyData[countyName] || 0;
                const fillColor = getColorForValue(value, maxValue);

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onClick={() => handleShapeClick(countyName)}
                    onMouseEnter={() => {
                      let suffix = "";
                      if (mapMode === "QHP") {
                        suffix = value <= 1 ? "Qhp" : "Qhps";
                      } else if (mapMode === "Medicaid") {
                        suffix = value <= 1 ? "Medicaid" : "Medicaids";
                      }
                      setTooltipContent(`${countyName}: ${value} ${suffix}`);
                    }}

                    onMouseLeave={() => {
                      setTooltipContent("");
                    }}
                    onMouseMove={(evt) => {
                      const mapContainer = evt.target.closest(".map-wrapper"); // Get the map container
                      if (!mapContainer) return;

                      const { left, top } = mapContainer.getBoundingClientRect(); // Get container position

                      setTooltipPos({
                        x: evt.clientX - left + 15, // Adjust X relative to container
                        y: evt.clientY - top - 20,  // Adjust Y to stay above the cursor
                      });
                    }}
                    style={{
                      default: {
                        fill: fillColor,
                        stroke: "#000",
                        strokeWidth: 0.5,
                        outline: "none",
                      },
                      hover: {
                        fill: "#2c4fa3", // highlight on hover
                        stroke: "#000",
                        strokeWidth: 1,
                        outline: "none",
                      },
                    }}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>
      )}

      {/* Tooltip */}
      {tooltipContent && (
        <div
          style={{
            position: "absolute",
            top: `${tooltipPos.y}px`, // 10px above cursor
            left: `${tooltipPos.x}px`, // 15px right of cursor
            background: "#fff",
            border: "1px solid #ccc",
            borderRadius: "4px",
            padding: "6px",
            pointerEvents: "none",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.3)",
            whiteSpace: "nowrap",
            zIndex: 999,
            transform: "translate(-5%, -90%)", // Adjusts for better alignment 
          }}
        >
          <strong>{tooltipContent}</strong>
        </div>
      )}
    </div>
  );
};

export default TexasMap;
